import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { SOURCE_MESSAGES } from '@his/shared';
import { useAuth } from '@/lib/auth';
import { connectedScreens, helpByKey, helpFor, tourLinkFor, type HelpLookup } from '@/lib/help/help-resolve';
import { helpMessage } from '@/lib/help/help-messages';
import { useTranslation } from '@/lib/i18n';
import { navItemKey } from '@/lib/navigation-i18n';
import { Sheet } from '@/components/ui/sheet';
import { HelpPanel } from './HelpPanel';
import { useHelp } from './HelpProvider';

const NOTHING_HERE = 'There is no help written for this screen yet.';

export function HelpSheet() {
  const help = useHelp();
  const { pathname } = useLocation();
  const { can, hasModule, isRetired } = useAuth();
  const shellText = useTranslation('shell');
  const t = shellText.t;
  const translated = shellText.translated && shellText.locale !== 'en';

  const lookup = useMemo<HelpLookup>(
    () => (key) => (translated && key in SOURCE_MESSAGES.shell ? t(key) : helpMessage(key)),
    [translated, t],
  );

  const request = help?.request ?? {};
  const topic = useMemo(
    () => (request.key ? helpByKey(request.key, lookup) : helpFor(pathname, request.tab, lookup)),
    [request.key, request.tab, pathname, lookup],
  );

  const screens = useMemo(
    () =>
      topic
        ? connectedScreens(topic, can, hasModule, isRetired, (item) => {
            const key = navItemKey(item.to);
            return translated && key in SOURCE_MESSAGES.shell ? t(key) : item.label;
          })
        : [],
    [topic, can, hasModule, isRetired, translated, t],
  );

  if (!help) return null;

  const navTitle = topic?.navLabel ? (translated && navItemKey(topic.route) in SOURCE_MESSAGES.shell ? t(navItemKey(topic.route)) : topic.navLabel) : undefined;
  const title = topic?.title || navTitle || 'Help';
  const description = topic && topic.title && navTitle && navTitle !== topic.title ? navTitle : undefined;

  return (
    <Sheet open={help.isOpen} onClose={help.close} title={title} description={description}>
      {topic ? <HelpPanel topic={topic} screens={screens} tour={tourLinkFor(topic)} /> : <p className="text-sm text-muted-foreground">{NOTHING_HERE}</p>}
    </Sheet>
  );
}
